import { Suspense, useEffect } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import LoadingAnim from "./components/LoadingAnim";
import ErrorBoundary from "./functional-components/ErrorBoundary";
import ErrorDisplay from "./components/ErrorDisplay";
import {
  QueryClient,
  QueryClientProvider,
  QueryErrorResetBoundary
} from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import inactiveReloading from "./utils/inactiveReloading";
import { getBuildVersion } from "./utils/VersionUtils";
import "@af-utils/scrollend-polyfill";
import { RouterProvider } from "react-router";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router-dom";
import { getCurrentSiteDefaultLanguage } from "./utils/SiteUtils";
import Layout from "./layout";

Sentry.init({
  dsn: "https://313864bde5794838817e003709b3a78d@o449769.ingest.sentry.io/5433315",
  integrations: [
    Sentry.replayIntegration(),
    Sentry.browserSessionIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.captureConsoleIntegration({
      handled: false,
      levels: ["error"]
    })
  ],
  attachStacktrace: true,
  tracesSampleRate: 0.05,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  environment: window.location.hostname,
  release: import.meta.env.VITE_VERSION || undefined,
  ignoreErrors: [
    //Note: When using strings, they’ll partially match the messages
    "TypeError: Network request failed",
    "Failed to fetch",
    "NetworkError",
    "Network Error",
    "Request aborted",
    "ResizeObserver loop limit exceeded",
    "Object Not Found Matching Id" //See https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/17
  ],
  denyUrls: [
    /:\/\/localhost/,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i
  ],
  beforeSend(event, hint) {
    //See: https://github.com/getsentry/sentry-javascript/issues/2514
    if (hint.originalException === "Timeout") return null;
    return event;
  }
});

Sentry.setUser({ ip_address: "{{auto}}" });
getBuildVersion().then(buildVersion => {
  Sentry.setTag("bjelin_web_version", buildVersion);
});

inactiveReloading();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
      staleTime: Infinity,
      refetchInterval: 1000 * 3600 * 3
    }
  }
});

const container = document.getElementById("root");
const root = createRoot(container!, {
  onRecoverableError: Sentry.reactErrorHandler()
});

const App = ({ isPrint = false }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            boundaryId="root"
            onReset={reset}
            fallback={<ErrorDisplay />}
          >
            <Suspense fallback={isPrint ? null : <LoadingAnim log={"root"} />}>
              <Layout print={isPrint} />
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

const pathParts = (location.pathname || "/").split("/");
const pathLang = pathParts[1];
const basename = "/" + pathLang || getCurrentSiteDefaultLanguage();
console.log("Router basename", basename);
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
const router = sentryCreateBrowserRouter(
  [{ path: "*", element: <App isPrint={pathLang === "print"} /> }],
  {
    basename
  }
);

root.render(
  <RouterProvider
    router={router}
    fallbackElement={
      pathLang === "print" ? null : (
        <LoadingAnim log={"RouterProvider.fallbackElement"} />
      )
    }
  />
);
